import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import colors from 'config/theme/colors';
import Tooltip from 'ui/base/Tooltip';

import Thumbnail from './Thumbnail';
import { ImportedFile, ImportedFileButton, ColoredIcon, ImportedFilesMask, MediaButton, ErrorByMedia, ErrorBadge } from './UI';

/** @typedef {{
 *  media: import('modules/mediaLibrary/types').UploadModalMedia,
 *  onUpdate: (id: string) => void,
 *  onDelete: (id: string) => void
 *  error: React.ReactNode | undefined
 *  }} ImportedMediaCardProps */
/** @type {React.FC<ImportedMediaCardProps>} */
const ImportedMediaCard = ({ media, onUpdate, error: validationError, onDelete }) => {
  const [isHovering, setIsHovering] = React.useState(false);

  const uploadFailed = media.isUploaded === false;
  const shouldUpdate = typeof onUpdate === 'function' && !media.isMediaLibraryItem && !validationError;

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const handleClick = () => onUpdate(media.id);

  const handleDelete = () => onDelete(media.id);

  return (
    <ImportedFile
      key={media.id}
      onMouseOut={handleMouseOut}
      onMouseOver={handleMouseOver}
      className={classNames({ error: uploadFailed })}
    >
      {'isUploaded' in media && (
        <ImportedFilesMask isUploaded={media.isUploaded}>
          <i className={media.isUploaded ? 'fb-icon--check' : 'fb-icon--status-urgent'} />
        </ImportedFilesMask>
      )}
      <Thumbnail media={media.isMediaLibraryItem ? media.media : media} />
      <ErrorByMedia visible={Boolean(validationError)}>
        <ErrorBadge>
          <FormattedMessage tagName="span" id="mediaLibrary.error" defaultMessage="Error" />
          <Tooltip title={validationError} placement="top">
            <span className="fb-icon--info error-icon" />
          </Tooltip>
        </ErrorBadge>
      </ErrorByMedia>
      <MediaButton visible={isHovering && !uploadFailed}>
        {shouldUpdate && (
          <ImportedFileButton className="mr-2" onClick={handleClick}>
            <ColoredIcon className="fb-icon--edit" color={colors['@grey-dark-elevated']} />
          </ImportedFileButton>
        )}
        <ImportedFileButton onClick={handleDelete}>
          <ColoredIcon className="fb-icon--close" color={colors['@error-color']} />
        </ImportedFileButton>
      </MediaButton>
    </ImportedFile>
  );
};

export default ImportedMediaCard;
